import { useState } from 'react';
import { Flag, ShieldAlert, VolumeX } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { reportBattleRoom, postBattleEvent } from '../lib/battle-client';

type BattleSafetyControlsProps = {
  roomId: string;
  targetUserId?: string;
  accessToken?: string | null;
};

export function BattleSafetyControls({ roomId, targetUserId, accessToken }: BattleSafetyControlsProps) {
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const runAction = async (action: () => Promise<void>, successMessage: string) => {
    setStatusMessage(null);
    try {
      await action();
      setStatusMessage(successMessage);
    } catch (error: any) {
      setStatusMessage(error?.message || 'Action failed');
    }
  };

  return (
    <Card className="p-4 border-red-200/60 dark:border-red-900/40 bg-white/90 dark:bg-neutral-950/70">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2 mb-3">
        <ShieldAlert className="w-4 h-4 text-red-500" />
        Safety controls
      </h3>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-300"
          disabled={!targetUserId || !accessToken}
          onClick={() =>
            void runAction(
              async () => {
                if (!targetUserId) throw new Error('No opponent to mute');
                await postBattleEvent(
                  roomId,
                  { type: 'mute', targetUserId, idempotencyKey: crypto.randomUUID() },
                  accessToken,
                );
              },
              'Opponent muted for this session.',
            )
          }
        >
          <VolumeX className="w-4 h-4 mr-2" />
          Mute opponent
        </Button>
        <Button
          type="button"
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300"
          disabled={!targetUserId || !accessToken}
          onClick={() =>
            void runAction(
              async () => {
                if (!targetUserId) throw new Error('No opponent to block');
                await postBattleEvent(
                  roomId,
                  { type: 'block', targetUserId, idempotencyKey: crypto.randomUUID() },
                  accessToken,
                );
              },
              'Opponent blocked for this battle.',
            )
          }
        >
          <ShieldAlert className="w-4 h-4 mr-2" />
          Block opponent
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/30"
          disabled={!accessToken}
          onClick={() =>
            void runAction(
              async () => {
                await reportBattleRoom(roomId, 'Inappropriate behavior during live battle', accessToken);
              },
              'Report sent to moderation queue.',
            )
          }
        >
          <Flag className="w-4 h-4 mr-2" />
          Report battle
        </Button>
      </div>
      {statusMessage && (
        <p className="mt-3 text-xs text-neutral-600 dark:text-neutral-300">{statusMessage}</p>
      )}
    </Card>
  );
}
